import { apolloClient } from '@/plugins/apollo';
import { FullTextReviewOutcome } from '@/models';
import { ScreeningOutcomeResolution, AssessmentState } from '@/enums';
import {
  CreateFullTextReviewOutcome,
  ChangeFullTextReviewOutcome,
  RemoveFullTextReviewOutcomeConflictResolution,
  UndoFullTextReviewOutcomeManualOverride,
} from '@/graphql';
import { v4 as createUUID } from 'uuid';

const actions = {
  async save(context, fullTextReviewOutcome) {
    return context.dispatch('internalSave', {
      fullTextReviewOutcome,
    });
  },
  async resolveConflictOptimistically(
    context,
    { reference, outcome, resolvedBy, reasonReviewCriteria, notes }
  ) {
    return context.dispatch(
      'saveOptimistically',
      new FullTextReviewOutcome({
        reference,
        outcome,
        resolvedBy,
        resolution: ScreeningOutcomeResolution.CONFLICT_RESOLUTION,
        reasonReviewCriteria,
        notes,
      })
    );
  },
  async removeConflictResolution(context, { id, cacheUpdate }) {
    const response = await apolloClient.mutate({
      mutation: RemoveFullTextReviewOutcomeConflictResolution,
      variables: {
        id,
      },
      update(cache, { data }) {
        cacheUpdate(
          cache,
          data.removeFullTextReviewOutcomeConflictResolution
        );
      },
    });
    return new FullTextReviewOutcome(
      response.data.removeFullTextReviewOutcomeConflictResolution
    );
  },
  async saveOptimistically(context, fullTextReviewOutcome) {
    return context.dispatch('internalSave', {
      fullTextReviewOutcome,
      optimistic: true,
    });
  },
  async internalSave(
    context,
    { fullTextReviewOutcome, optimistic = false }
  ) {
    const mutation = fullTextReviewOutcome.id
      ? ChangeFullTextReviewOutcome
      : CreateFullTextReviewOutcome;
    const key = fullTextReviewOutcome.id
      ? 'changeFullTextReviewOutcome'
      : 'createFullTextReviewOutcome';

    let optimisticResponse;
    let refetchQueries;

    if (optimistic) {
      const optimisticId = createUUID();
      optimisticResponse = {
        [key]: {
          ...fullTextReviewOutcome,
          reference: {
            ...fullTextReviewOutcome.reference,
            fullTextReviewState: AssessmentState.COMPLETE,
          },
          id: optimisticId,
          optimisticId,
        },
      };
      optimisticResponse[key].reference.fullTextReviewOutcome = {
        ...optimisticResponse[key],
        reference: null,
      };
    } else {
      refetchQueries = [
        'GetReferencesByReviewIdAndFilters',
        'GetReferencesByFilters',
        'GetFullTextReviewConflictsPage',
        'SearchReferences',
      ];
    }

    const response = await apolloClient.mutate({
      mutation,
      variables: {
        fullTextReviewOutcome: fullTextReviewOutcome.creationData(),
      },
      optimisticResponse,
      update(cache, { data }) {
        cache.modify({
          id: cache.identify(fullTextReviewOutcome.reference),
          fields: {
            fullTextReviewOutcome(_, { toReference }) {
              return toReference(data[key]);
            },
          },
        });
      },
      refetchQueries,
      awaitRefetchQueries: !optimistic,
    });
    return new FullTextReviewOutcome(response.data[key]);
  },
  async undoManualOverride(
    context,
    { id, cacheUpdate, optimisticResponse }
  ) {
    let optimisticResponseToUse = undefined;
    if (optimisticResponse) {
      optimisticResponseToUse = {
        undoFullTextReviewOutcomeManualOverride: {
          ...optimisticResponse,
          optimisticId: createUUID(),
        },
      };
    }
    const response = await apolloClient.mutate({
      mutation: UndoFullTextReviewOutcomeManualOverride,
      variables: {
        id,
      },
      optimisticResponse: optimisticResponseToUse,
      update(cache, { data }) {
        const response = data.undoFullTextReviewOutcomeManualOverride;
        cacheUpdate(cache, response);
      },
    });
    return new FullTextReviewOutcome(
      response.data.undoFullTextReviewOutcomeManualOverride
    );
  },
};

export default {
  namespaced: true,
  actions,
};
